// Step 3: the mapping grid — the notices explaining why there may be nothing to
// map, the progress toolbar, and one collapsible section per source integration.

import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@decisiv/ui-components';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import { t, Trans } from '@lingui/macro';
import CheckCircleF from '@decisiv/iconix/lib/components/CheckCircleF';
import ChevronRight from '@decisiv/iconix/lib/components/ChevronRight';
import ExclamationCircleF from '@decisiv/iconix/lib/components/ExclamationCircleF';

import {
  CountPill,
  DesignSection,
  DesignSectionHead,
  GroupCaret,
  GroupName,
  GroupSub,
  MappingGroup,
  MappingGroupHead,
  MappingGroupPanel,
  MappingWarning,
  MappingWarningText,
  MappingsNotice,
  MappingsToolbar,
  StepBadge,
  StepHeading,
  StepIntro,
  charcoalColor,
  mappingWarningColor,
} from '../style';
import { headerKeysShape } from './formPropTypes';
import MappingPairCard from './MappingPairCard';

const AttributeMappings = ({
  engine,
  headerKeysByAttr,
  noMappingsMade = false,
}) => {
  const {
    pairGroups,
    mappableIntegrations,
    lifecycleOnlyIntegrations,
    statsBySource,
    openIds,
    allGroupsOpen,
    toggleGroup,
    toggleAllGroups,
    mappingsForPair,
    changeMapping,
    mappedCount,
    totalMappings,
  } = engine;

  return (
    <DesignSection>
      <DesignSectionHead>
        <StepHeading>
          <StepBadge>3</StepBadge>
          <Trans>Attribute Mappings</Trans>
        </StepHeading>
        <StepIntro>
          <Trans>
            Source attributes are listed automatically. When the target
            integration has an attribute with the same name it is pre-selected;
            otherwise choose the matching target attribute from the dropdown, or
            leave blank to skip.
          </Trans>
        </StepIntro>
      </DesignSectionHead>
      <div>
        {pairGroups.length === 0 && (
          <MappingsNotice>
            <CheckCircleF color={charcoalColor} />
            <P size="small" shade={1} marginBottom={0}>
              {mappableIntegrations.length === 0 ? (
                <Trans>
                  No attribute mappings are required — every selected
                  integration is <strong>Lifecycle</strong> sync only, so there
                  is nothing to pair.
                </Trans>
              ) : (
                <Trans>
                  No attribute mappings are required — only{' '}
                  <strong>{mappableIntegrations[0].attributes.name}</strong> has
                  location-level attributes, and a mapping needs two.
                </Trans>
              )}
            </P>
          </MappingsNotice>
        )}

        {pairGroups.length > 0 && lifecycleOnlyIntegrations.length > 0 && (
          <MappingsNotice>
            <P size="small" shade={1} marginBottom={0}>
              <Trans>
                Skipped — <strong>Lifecycle</strong> sync only, no attributes to
                map:
              </Trans>{' '}
              {lifecycleOnlyIntegrations
                .map((i) => i.attributes.name)
                .join(', ')}
            </P>
          </MappingsNotice>
        )}

        {pairGroups.length > 0 && (
          <MappingsToolbar>
            <CountPill warn={mappedCount < totalMappings}>
              {mappedCount} / {totalMappings} <Trans>mapped</Trans>
            </CountPill>
            {pairGroups.length > 1 && (
              <Button
                text={allGroupsOpen ? t`Collapse all` : t`Expand all`}
                kind="secondary"
                variant="ghost"
                size="small"
                onClick={toggleAllGroups}
              />
            )}
          </MappingsToolbar>
        )}

        {noMappingsMade && (
          <MappingWarning>
            <ExclamationCircleF color={mappingWarningColor} />
            <MappingWarningText>
              <Trans>
                At least one attribute mapping is required. Select a target
                attribute for one or more source attributes.
              </Trans>
            </MappingWarningText>
          </MappingWarning>
        )}

        {pairGroups.map(({ integration, groupPairs }) => {
          const { mapped, total } = statsBySource[integration.id] ?? {
            mapped: 0,
            total: 0,
          };
          const isOpen = openIds.includes(integration.id);
          const panelId = `mapping-group-${integration.id}`;

          return (
            <MappingGroup key={integration.id}>
              <MappingGroupHead
                onClick={() => toggleGroup(integration.id)}
                aria-expanded={isOpen}
                // The panel is only in the DOM while open, so pointing
                // aria-controls at it when collapsed would be invalid.
                aria-controls={isOpen ? panelId : undefined}
              >
                <GroupCaret expanded={isOpen ? 1 : 0}>
                  <ChevronRight size="small" />
                </GroupCaret>
                <GroupName>{integration.attributes.name}</GroupName>
                <GroupSub>
                  {groupPairs.length === 1 ? (
                    <Trans>1 pair</Trans>
                  ) : (
                    <Trans>{groupPairs.length} pairs</Trans>
                  )}
                </GroupSub>
                <CountPill warn={mapped < total}>
                  {mapped} / {total} <Trans>mapped</Trans>
                </CountPill>
              </MappingGroupHead>
              {isOpen && (
                <MappingGroupPanel id={panelId}>
                  {groupPairs.map(([source, target]) => (
                    <MappingPairCard
                      key={`${source.id}-${target.id}`}
                      source={source}
                      target={target}
                      pairMappings={mappingsForPair(source.id, target.id)}
                      headerKeysByAttr={headerKeysByAttr}
                      onChange={changeMapping}
                    />
                  ))}
                </MappingGroupPanel>
              )}
            </MappingGroup>
          );
        })}
      </div>
    </DesignSection>
  );
};

AttributeMappings.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  engine: PropTypes.object.isRequired,
  headerKeysByAttr: headerKeysShape.isRequired,
  noMappingsMade: PropTypes.bool,
};
export default memo(AttributeMappings);
